import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { NotificationService, NotificationGridConfig } from '@core/services/notification.service';
import { ApiResponse, NotificationSummary, NotificationGridResult } from '@core/types';

@Injectable({ providedIn: 'root' })
export class NotificationQuery {
  private readonly service = inject(NotificationService);
  private readonly queryClient = inject(QueryClient);

  useSummary() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.notifications.summary(),
      queryFn: () => lastValueFrom(this.service.getSummary()),
      select: (res: ApiResponse<NotificationSummary>) => res.data,
      staleTime: environment.staleTime,
      refetchInterval: 60_000,
    }));
  }

  useGrid(config: () => NotificationGridConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.notifications.grid(c),
        queryFn: () => lastValueFrom(this.service.getGrid(c)),
        select: (res: ApiResponse<NotificationGridResult>) =>
          res.data ?? { list: [], totalRecord: 0, pageCount: 0 },
        staleTime: environment.staleTime,
      };
    });
  }

  useMarkAsRead() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.markAsRead(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.all });
      },
    }));
  }

  useMarkAllAsRead() {
    return injectMutation(() => ({
      mutationFn: () => lastValueFrom(this.service.markAllAsRead()),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.all });
      },
    }));
  }
}
